// The IDE's project model + localStorage persistence.
//
// A "project" is one deployment being authored: a name, its ordered members
// (each a `.munos` label + source), which member is active, and a reference to
// the ROM it was last run against. The whole thing autosaves to localStorage
// on every edit, so a reload or crash never loses work.

export interface ProjectMember {
  label: string;
  source: string;
}

export interface Project {
  name: string;
  members: ProjectMember[];
  activeLabel: string;
  rom: { hash: string; name: string } | null;
}

const STORAGE_KEY = "mn-dev-project:v1";

// A single-member starter that draws a moving box over any ROM — enough to
// confirm the compile→run→render loop end to end on first open. The `server`
// half is present but inert (singleplayer still has a server connection — a
// fetch IS a connection, architecture.md).
const SEED_SOURCE = `// hello.munos — a starter Munos overlay.
//
// This draws a small white box sliding across the screen, on top of whatever
// ROM you load. Edit it and press Play (or Ctrl+Enter) to see changes live.
// A deployment can have several members — use "+ Member" to add more; each
// member's own client{}/server{} blocks decide where its code runs.

client {
    // An 8x8 image, every pixel palette index 1 (base64 of 64 bytes of 0x01).
    var box: image = image_literal(8, 8,
        "AQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==")
    // Index 0 is transparent; index 1 is white (0xAARRGGBB).
    var pal: u32[] = [0, 0xFFFFFFFF]

    var x: i32 = 0
    var dir: i32 = 1

    event frame(frame_num: i32) {
        x = x + dir
        if x > 240 { dir = -1 }
        if x < 0 { dir = 1 }
        draw_image(box, pal, x, 40)
    }
}

server {
    event tick(tick_num: i32) { }
}
`;

export function seedProject(): Project {
  return {
    name: "untitled",
    members: [{ label: "hello", source: SEED_SOURCE }],
    activeLabel: "hello",
    rom: null,
  };
}

export function loadProject(): Project {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return seedProject();
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      Array.isArray((parsed as Project).members) &&
      (parsed as Project).members.length > 0
    ) {
      const p = parsed as Project;
      // Defensive: make sure activeLabel points at a real member.
      if (!p.members.some((m) => m.label === p.activeLabel)) {
        p.activeLabel = p.members[0]!.label;
      }
      return p;
    }
  } catch {
    /* corrupt/absent — fall through to the seed */
  }
  return seedProject();
}

export function saveProject(project: Project): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(project));
  } catch {
    /* quota / private-mode — non-fatal, the editor still works this session */
  }
}

/** A member label that doesn't collide with any existing one. */
export function uniqueLabel(members: ProjectMember[], base = "member"): string {
  if (!members.some((m) => m.label === base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}${i}`;
    if (!members.some((m) => m.label === candidate)) return candidate;
  }
}
